import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Divider, IconButton, ListItemText, Menu, MenuItem, Box } from '@mui/material';
import { useRef, useState } from 'react';
import type { TPhoenixColumn } from './../utility/types';

interface IColumnHeaderMenuProps {
    column: TPhoenixColumn;
    onOpenFilter: (anchorEl: HTMLElement, columnId: string) => void;
    onOpenShowColumns: (anchorEl: HTMLElement) => void;
}
export const ColumnHeaderMenu = ({ column, onOpenFilter, onOpenShowColumns }: IColumnHeaderMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const pendingOpenRef = useRef<(() => void) | null>(null);
    const close = () => setAnchorEl(null);
    const sortDirection = column.getIsSorted(); // 'asc' | 'desc' | false
    const pinned = column.getIsPinned(); // 'start' | 'end' | false

    return (
        <>
            <Box
                sx={{
                    '& .more-icon': {
                        visibility: 'hidden',
                    },
                    '&:hover .more-icon': {
                        visibility: 'visible',
                    },
                }}
            >
                <IconButton
                    className="more-icon"
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        setAnchorEl(event.currentTarget);
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={close}
                TransitionProps={{
                    onExited: () => {
                        const openPanel = pendingOpenRef.current;
                        pendingOpenRef.current = null;
                        openPanel?.();
                    },
                }}
            >
                {column.getCanSort() && [
                    <MenuItem
                        key="unsort"
                        disabled={!sortDirection}
                        onClick={() => {
                            column.clearSorting();
                            close();
                        }}
                    >
                        <ListItemText>Unsort</ListItemText>
                    </MenuItem>,
                    <MenuItem
                        key="asc"
                        disabled={sortDirection === 'asc'}
                        onClick={() => {
                            column.toggleSorting(false);
                            close();
                        }}
                    >
                        <ListItemText>Sort by ASC</ListItemText>
                    </MenuItem>,
                    <MenuItem
                        key="desc"
                        disabled={sortDirection === 'desc'}
                        onClick={() => {
                            column.toggleSorting(true);
                            close();
                        }}
                    >
                        <ListItemText>Sort by DESC</ListItemText>
                    </MenuItem>,
                    <Divider key="sort-divider" />,
                ]}

                {column.getCanFilter() && (
                    <MenuItem
                        onClick={() => {
                            const target = anchorEl;
                            if (target) {
                                pendingOpenRef.current = () => onOpenFilter(target, column.id);
                            }
                            close();
                        }}
                    >
                        <ListItemText>Filter</ListItemText>
                    </MenuItem>
                )}

                {column.getCanHide() && (
                    <MenuItem
                        onClick={() => {
                            column.toggleVisibility(false);
                            close();
                        }}
                    >
                        <ListItemText>Hide</ListItemText>
                    </MenuItem>
                )}

                <MenuItem
                    onClick={() => {
                        const target = anchorEl;
                        if (target) {
                            pendingOpenRef.current = () => onOpenShowColumns(target);
                        }
                        close();
                    }}
                >
                    <ListItemText>Show columns</ListItemText>
                </MenuItem>

                <Divider />

                {pinned ? (
                    <MenuItem
                        onClick={() => {
                            column.pin(false);
                            close();
                        }}
                    >
                        <ListItemText>Unpin</ListItemText>
                    </MenuItem>
                ) : (
                    [
                        <MenuItem
                            key="pin-start"
                            onClick={() => {
                                column.pin('start');
                                close();
                            }}
                        >
                            <ListItemText>Pin to left</ListItemText>
                        </MenuItem>,
                        <MenuItem
                            key="pin-end"
                            onClick={() => {
                                column.pin('end');
                                close();
                            }}
                        >
                            <ListItemText>Pin to right</ListItemText>
                        </MenuItem>,
                    ]
                )}
            </Menu>
        </>
    );
};